import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { webMercator } from "@evergis/sgis/es/Crs";
import { PointFeature } from "@evergis/sgis/es/features/PointFeature";

import { MaskedSymbols } from "../../../react-sgis/symbols/SymbolRenderer/defaultSymbols";
import { DEFAULT_PROJECT_CONTENT } from "../../../evergis/core/project/createProjectContent";
import { setMapView } from "../../../ducks/mapView/mapView";
import { createLabelSymbol } from "../../../react-sgis/symbols/labelSymbols/labelSymbols";
import { getAppConfig } from "../../../ducks/appConfig";
import { useMapView } from "../../../ducks/mapView/useMapView";
import { GeoLocationMode } from "../components/GeoLocation/types";

const { resolution } = DEFAULT_PROJECT_CONTENT;

export const useGeoLocation = () => {
  const dispatch = useDispatch();
  const { settings } = useSelector(getAppConfig);
  const [mapView] = useMapView();
  const [feature, setFeature] = useState([]);
  const [mode, setMode] = useState(GeoLocationMode.Off);
  const watchRef = useRef<number>();
  const defaultResolution = settings?.geoLocationResolution || resolution;

  const errorPosition = useCallback(error => {
    console.warn("Error in navigator.geolocation", error);

    if (navigator.geolocation) {
      navigator.geolocation.clearWatch(watchRef.current);
    }
  }, []);

  const updatePosition = useCallback(
    newPos => {
      const coords = [newPos.coords.latitude, newPos.coords.longitude];
      const projected = new PointFeature(coords as [number, number]).projectTo(webMercator);
      const { position } = projected;

      if (
        feature?.length &&
        mapView.position[0] === position[0] &&
        mapView.position[1] === position[1] &&
        mapView.resolution === defaultResolution
      ) {
        setFeature([]);
        setMode(GeoLocationMode.Off);
      } else {
        setFeature(() => [
          new PointFeature(position, {
            crs: webMercator,
            persistOnMap: true,
            symbol: createLabelSymbol("pinSymbol", {
              symbol: MaskedSymbols.geoLocationPin,
              size: 32,
              offset: [-16, -32],
            }),
          }),
        ]);

        dispatch(setMapView({ position, resolution: defaultResolution }));
        setMode(GeoLocationMode.Watching);
      }
    },
    [defaultResolution, dispatch, feature?.length, mapView.position, mapView.resolution],
  );

  const watchGeoLocation = useCallback(() => {
    if (navigator.geolocation) {
      if (watchRef.current) {
        navigator.geolocation.clearWatch(watchRef.current);
        navigator.geolocation.getCurrentPosition(updatePosition, errorPosition);
      }

      watchRef.current = navigator.geolocation.watchPosition(updatePosition, errorPosition, { timeout: 2000 });
    } else {
      console.warn("The browser doesn't support navigator.geolocation");
    }
  }, [errorPosition, updatePosition]);

  useEffect(() => {
    if (!watchRef.current) return;

    if (mode === GeoLocationMode.Watching) {
      navigator.geolocation.clearWatch(watchRef.current);
      setMode(GeoLocationMode.On);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapView.position?.[0], mapView.position?.[1]]);

  useEffect(
    () => () => {
      navigator.geolocation.clearWatch(watchRef.current);
    },
    [],
  );

  return {
    geoLocationFeature: feature,
    geoLocationMode: mode,
    watchGeoLocation,
  };
};
